import type { FinanceCountryGrossDTO } from '@/api/generated/model';
import { formatMoneyWithCode } from '@/lib/money';
import { financeCountryLabel, UNASSIGNED_COUNTRY_CODE } from '../lib/financeColors';

function num(value: number | string | null | undefined): number {
  if (value == null) return 0;
  return typeof value === 'number' ? value : Number(value);
}

export function FinanceCountryGrossBreakdown({
  breakdown,
  currency,
  selectedCountryCode,
  onSelectCountryCode,
}: {
  breakdown: FinanceCountryGrossDTO[];
  currency: string;
  selectedCountryCode: string;
  onSelectCountryCode: (countryCode: string) => void;
}) {
  if (breakdown.length === 0) return null;

  const total = breakdown.reduce((sum, row) => sum + num(row.amount), 0);

  return (
    <div className="rounded-md border border-border/60 bg-card/40 px-4 py-3 text-xs leading-5">
      <p className="font-medium text-foreground">
        Creator country (Connect) Gross = sum of Connect-country buckets
      </p>
      <p className="mt-1 text-[11px] text-muted-foreground">
        Filtered by creator Stripe Connect country (<code>users.country_code</code>), not buyer
        geography. AU + US alone will not equal All when other countries or Not yet onboarded
        to Stripe (no Connect country set) exist. Click a bucket to filter.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          className={`rounded-md border px-2.5 py-1.5 text-left transition-colors ${
            selectedCountryCode === ''
              ? 'border-sky-500/50 bg-sky-500/15 text-sky-100'
              : 'border-border/70 bg-background/40 hover:bg-accent'
          }`}
          onClick={() => onSelectCountryCode('')}
        >
          <span className="block text-[10px] uppercase tracking-wide text-muted-foreground">
            All
          </span>
          <span className="font-medium">{formatMoneyWithCode(total, currency)}</span>
        </button>
        {breakdown.map((row) => {
          const code = row.countryCode ?? UNASSIGNED_COUNTRY_CODE;
          const selected = selectedCountryCode === code;
          return (
            <button
              key={code}
              type="button"
              className={`rounded-md border px-2.5 py-1.5 text-left transition-colors ${
                selected
                  ? 'border-sky-500/50 bg-sky-500/15 text-sky-100'
                  : 'border-border/70 bg-background/40 hover:bg-accent'
              }`}
              onClick={() => onSelectCountryCode(code)}
            >
              <span className="block text-[10px] uppercase tracking-wide text-muted-foreground">
                {row.label ?? financeCountryLabel(code)}
                {row.percentage != null ? ` · ${num(row.percentage).toFixed(1)}%` : ''}
              </span>
              <span className="font-medium">{formatMoneyWithCode(row.amount, currency)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
